import React, {
    PureComponent,
    Fragment,
    Children,
    ReactElement,
    ReactNode,
    FunctionComponent,
} from 'react';

import {ShadowCard} from '@optimaxdev/design-system';

import {Step, StepType} from '../step/step';

import s from './progressBar.scss';

type PropsType = {
    activeStep?: number;
    children: ReactElement<StepType>[] | ReactElement<StepType>;
};

/**
 * Status bar display current status of client order
 */
export class ProgressBar extends PureComponent<PropsType> {
    static defaultProps = {
        activeStep: -1,
    };

    /**
     * Class constructor, validate ProgressBar childes
     * @param {PropsType} props - component props
     */
    constructor(props: PropsType) {
        super(props);
        /* Check that all children of ProgressBar is Steps */
        if (
            !(Children.toArray(props.children) as ReactElement<StepType>[]).every(
                item => (item.type as FunctionComponent<StepType>).displayName === 'Step',
            )
        ) {
            throw new TypeError('Every child in ProgressBar must be Step');
        }
    }

    /**
     * Return content of current step
     * @return {ReactNode | null} - of current step
     */
    getActiveStepContent = (): ReactNode | null => {
        const {activeStep, children} = this.props;
        const step = Children.toArray(children)[activeStep as number];

        return step ? (step as ReactElement<StepType>).props.children : null;
    };

    /**
     * Return progress bar width
     * @return {number} progress bar width
     */
    getProgressWidth = (): number => {
        const {activeStep = -1, children} = this.props;

        if (!Array.isArray(children)) return 0;

        return children.length < 2 || activeStep < 0
            ? 0
            : (100 / (children.length - 1)) * activeStep;
    };

    /**
     * Render component
     * @return {Node} - component
     */
    render() {
        const {activeStep = -1, children} = this.props;
        const stepContent = this.getActiveStepContent();

        return (
            <Fragment>
                <div className={s.wrapper}>
                    <div className={s.namesContainer}>
                        {(Children.toArray(children) as ReactElement<StepType>[]).map(step => (
                            <div className={s.name} key={step.key}>
                                {step.props.title}
                            </div>
                        ))}
                    </div>
                    <div className={s.lineContainer}>
                        <div className={s.line} />
                        <div
                            className={s.activeLine}
                            data-testid="activeLine"
                            style={{width: `${this.getProgressWidth()}%`}}
                        />
                        <div className={s.steps}>
                            {(Children.toArray(children) as ReactElement<StepType>[]).map(
                                (step, index: number) => (
                                    <Step
                                        {...step.props}
                                        withContent={
                                            Boolean(step.props.children) && activeStep === index
                                        }
                                        active={index <= activeStep}
                                        key={step.key}
                                    />
                                ),
                            )}
                        </div>
                    </div>
                </div>
                {stepContent && (
                    <div className={s.contentWrapper}>
                        <ShadowCard className={s.cardContainer} corner={4} saturate={2}>
                            {stepContent}
                        </ShadowCard>
                    </div>
                )}
            </Fragment>
        );
    }
}
